class PCMAudioPlayer {
    private sampleRate: number;
    private audioContext: AudioContext | null;
    private audioQueue: ArrayBuffer[];
    private isPlayingFlag: boolean;
    private currentSource: AudioBufferSourceNode | null;
    private onEndedCallback: (() => void) | null;

    constructor(sampleRate: number) {
        this.sampleRate = sampleRate;
        this.audioContext = null;
        this.audioQueue = [];
        this.isPlayingFlag = false;
        this.currentSource = null;
        this.onEndedCallback = null;
    }

    connect() {
        if (!this.audioContext) {
            this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        }
    }

    pushPCM(arrayBuffer: ArrayBuffer) {
        this.audioQueue.push(arrayBuffer);
        this._playNextAudio();
    }

    /**
     * 设置播放结束回调
     */
    onEnded(callback: () => void) {
        this.onEndedCallback = callback;
    }

    /**
     * 检查是否正在播放
     */
    isPlaying(): boolean {
        return this.isPlayingFlag;
    }

    /**
     * 将arrayBuffer转为audioBuffer
     */
    private _bufferPCMData(pcmData: ArrayBuffer): AudioBuffer {
        const sampleRate = this.sampleRate; // 设置为 PCM 数据的采样率
        const length = pcmData.byteLength / 2; // 假设 PCM 数据为 16 位，需除以 2
        
        if (!this.audioContext) {
            throw new Error("AudioContext not initialized");
        }
        
        const audioBuffer = this.audioContext.createBuffer(1, length, sampleRate);
        const channelData = audioBuffer.getChannelData(0);
        const int16Array = new Int16Array(pcmData); // 将 PCM 数据转换为 Int16Array

        for (let i = 0; i < length; i++) {
            // 将 16 位 PCM 转换为浮点数 (-1.0 到 1.0)
            channelData[i] = int16Array[i] / 32768; // 16 位数据转换范围
        }
        
        return audioBuffer;
    }

    private async _playAudio(arrayBuffer: ArrayBuffer) {
        if (!this.audioContext) {
            return;
        }
        
        if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }

        const audioBuffer = this._bufferPCMData(arrayBuffer);

        this.currentSource = this.audioContext.createBufferSource();
        this.currentSource.buffer = audioBuffer;
        this.currentSource.connect(this.audioContext.destination);

        this.currentSource.onended = () => {
            this.isPlayingFlag = false;
            this.currentSource = null;

            // 检查队列是否为空，如果为空则触发结束回调
            if (this.audioQueue.length === 0 && this.onEndedCallback) {
                this.onEndedCallback();
            } else {
                this._playNextAudio(); // Play the next audio in the queue
            }
        };
        
        this.currentSource.start();
        this.isPlayingFlag = true;
    }

    private _playNextAudio() {
        if (this.audioQueue.length > 0 && !this.isPlayingFlag) {
            // 计算总的字节长度
            const totalLength = this.audioQueue.reduce((acc, buffer) => acc + buffer.byteLength, 0);
            const combinedBuffer = new Uint8Array(totalLength);
            let offset = 0;

            // 将所有 audioQueue 中的 buffer 拼接到一个新的 Uint8Array 中
            for (const buffer of this.audioQueue) {
                combinedBuffer.set(new Uint8Array(buffer), offset);
                offset += buffer.byteLength;
            }

            // 清空 audioQueue，因为我们已经拼接完所有数据
            this.audioQueue = [];
            // 发送拼接的 audio 数据给 playAudio
            this._playAudio(combinedBuffer.buffer);
        }
    }
    
    stop() {
        if (this.currentSource) {
            this.currentSource.stop(); // 停止当前音频播放
            this.currentSource = null; // 清除音频源引用
            this.isPlayingFlag = false; // 更新播放状态
        }
        this.audioQueue = []; // 清空音频队列
    }
}

export default PCMAudioPlayer;
